import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Trophy, ChevronDown, ChevronUp, X, TrendingUp, Building2, HeartPulse, Baby, Landmark } from "lucide-react";

const TEMA = [
  { id: "helse", label: "Helse og omsorg", icon: HeartPulse },
  { id: "barn", label: "Barn og unge", icon: Baby },
  { id: "okonomi", label: "Kommunen og økonomien", icon: Landmark },
];
import { DelTopplisteKnapp } from "@/components/DelKnapp";
import { useSideTittel } from "@/hooks/useSideTittel";

const API = process.env.REACT_APP_BACKEND_URL;

const Liste = ({ liste, open, onToggle, spor }) => {
  const navigate = useNavigate();
  const rader = open ? liste.rader : liste.rader.slice(0, 10);
  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg" data-testid={`toppliste-${liste.id}`}>
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="text-base font-medium text-[#1F2A36]">{liste.tittel}</CardTitle>
          <DelTopplisteKnapp liste={liste.id} tittel={liste.tittel} spor={spor} testId={`del-toppliste-${liste.id}`} />
        </div>
        <p className="text-sm text-[#4B647F]">{liste.forklaring}</p>
        <p className="text-xs text-[#8AA4BF]">
          {liste.undertekst}
          {liste.nabo_namn || liste.fylke_namn
            ? ` · snitt her ${liste.snitt_vist ?? "—"} · kommunesnitt i landet ${liste.snitt_landet_vist ?? "—"}`
            : ` · kommunesnitt i landet ${liste.snitt_landet_vist ?? "—"}`}{" "}
          · tal for {liste.aar}
        </p>
      </CardHeader>
      <CardContent className="pt-4">
        <table className="w-full text-sm" data-testid={`toppliste-${liste.id}-tabell`}>
          <thead>
            <tr className="border-b border-[#E6E9F0]">
              <th className="w-10" />
              <th className="py-2 text-left text-xs font-medium uppercase tracking-[0.08em] text-[#8AA4BF]">Kommune</th>
              <th
                className="py-2 text-right text-xs font-medium uppercase tracking-[0.08em] text-[#8AA4BF]"
                data-testid={`toppliste-${liste.id}-kolonne`}
              >
                {liste.kolonne}
              </th>
            </tr>
          </thead>
          <tbody>
            {rader.length === 0 && (
              <tr>
                <td className="py-3 text-sm text-[#8AA4BF]" colSpan={3}>
                  Ingen kommunar med tal her
                </td>
              </tr>
            )}
            {rader.map((r) => (
              <tr
                key={r.kommune_code}
                className={`border-b border-[#F1F4F8] last:border-0 ${r.er_valgt ? "bg-[#FFF4F5]" : ""}`}
              >
                <td className="py-2 pr-2 text-[#8AA4BF] tabular-nums w-10">{r.rank}.</td>
                <td className="py-2">
                  <button
                    onClick={() => navigate(`/kommune/${r.kommune_code}`)}
                    className={`hover:underline text-left ${r.er_valgt ? "font-semibold text-[#E52437]" : "text-[#103766]"}`}
                    data-testid={`toppliste-${liste.id}-${r.kommune_code}`}
                  >
                    {r.namn}
                  </button>
                  {r.varsel && (
                    <span
                      className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#FFF4F5] border border-[#E52437]/30 px-2 py-0.5 text-[10px] font-semibold text-[#C0392B]"
                      title={`Lånegjelda har vakse ${r.vekst_pct} % sidan ${r.vekst_fra_aar}`}
                      data-testid={`gjeldsvarsel-${r.kommune_code}`}
                    >
                      <TrendingUp className="w-3 h-3" />+{Math.round(r.vekst_pct)} %
                    </span>
                  )}
                  {r.selskap_pct > 5 && (
                    <span
                      className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#F1F4F8] border border-[#C7D2DF] px-2 py-0.5 text-[10px] font-semibold text-[#103766]"
                      title="Så mykje meir gjeld har konsernet enn kommunekassa – gjeld som ligg i kommunale føretak og IKS"
                      data-testid={`selskapsgjeld-${r.kommune_code}`}
                    >
                      <Building2 className="w-3 h-3" />+{Math.round(r.selskap_pct)} % i selskap
                    </span>
                  )}
                  {r.gjeld_no && (
                    <span className="ml-2 text-[11px] text-[#8AA4BF]">
                      {r.gjeld_no.toLocaleString("nb-NO")} kr i dag
                    </span>
                  )}
                  {r.dagens_rente != null && (
                    <span className="ml-2 text-[11px] text-[#8AA4BF]">
                      betaler {String(r.dagens_rente).replace(".", ",")} % i dag
                    </span>
                  )}
                  {r.renteutgifter_mill != null && (
                    <span className="ml-2 text-[11px] text-[#8AA4BF]">
                      {r.renteutgifter_mill.toLocaleString("nb-NO")} mill. kr i renteutgifter
                    </span>
                  )}
                </td>
                <td
                  className={`py-2 text-right tabular-nums font-medium ${
                    liste.retning === "best" ? "text-[#1E7A4B]" : "text-[#C0392B]"
                  }`}
                >
                  {r.vist}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          onClick={onToggle}
          className="mt-3 text-xs text-[#103766] hover:underline inline-flex items-center gap-1"
          data-testid={`toppliste-${liste.id}-vis-fleire`}
        >
          {open ? <>Vis færre <ChevronUp className="w-3 h-3" /></> : <>Vis 25 på lista <ChevronDown className="w-3 h-3" /></>}
        </button>
        {liste.rader.some((r) => r.selskap_pct > 5) && (
          <p className="text-xs text-[#4B647F] mt-3" data-testid="selskapsgjeld-forklaring">
            «+X % i selskap»: så mykje meir gjeld har konsernet enn kommunekassa, altså gjeld som ligg i kommunale føretak og IKS
          </p>
        )}
        {liste.rader.some((r) => r.varsel) && (
          <p className="text-xs text-[#C0392B] mt-3" data-testid="gjeldsvarsel-forklaring">
            Raud markering: lånegjelda per innbyggjar har vakse meir enn 50 % sidan 2020
          </p>
        )}
        <p className="text-xs text-[#8AA4BF] mt-3">Kjelde: {liste.kjelde}</p>
      </CardContent>
    </Card>
  );
};

const TopplisterPage = () => {
  const [lister, setLister] = useState([]);
  const [fylker, setFylker] = useState([]);
  const [lastar, setLastar] = useState(true);
  const [opne, setOpne] = useState({});
  const [params, setParams] = useSearchParams();
  const [kommunar, setKommunar] = useState([]);
  const fylke = params.get("fylke") || "";
  const retning = params.get("retning") === "best" ? "best" : "verst";
  const nabo = params.get("nabo") || "";
  const navigate = useNavigate();
  useSideTittel(
    retning === "best"
      ? "Best i landet – topplister for norske kommunar"
      : "Verst i landet – topplister for norske kommunar",
    "Kommunane som kjem verst ut på barn per årsverk i barnehagen, elevar per lærar, brukarar per årsverk i omsorga, lånegjeld og barnefattigdom."
  );

  useEffect(() => {
    axios.get(`${API}/api/fylker`).then((r) => setFylker(r.data)).catch(() => setFylker([]));
    axios.get(`${API}/api/kommuner`).then((r) => setKommunar(r.data)).catch(() => setKommunar([]));
  }, []);

  const spor = [fylke && `fylke=${fylke}`, retning === "best" && "retning=best", nabo && `nabo=${nabo}`]
    .filter(Boolean)
    .join("&");

  useEffect(() => {
    setLastar(true);
    axios
      .get(`${API}/api/topplister${spor ? `?${spor}` : ""}`)
      .then((r) => setLister(r.data.lister))
      .catch(() => setLister([]))
      .finally(() => setLastar(false));
  }, [spor]);

  const bytt = (nokkel, verdi) => {
    const neste = new URLSearchParams(params);
    if (verdi) neste.set(nokkel, verdi);
    else neste.delete(nokkel);
    if (nokkel === "nabo" && verdi) neste.delete("fylke");
    if (nokkel === "fylke" && verdi) neste.delete("nabo");
    setParams(neste, { replace: true });
  };

  const fylkeNamn = fylker.find((f) => f.fylke_code === fylke)?.fylke_name;
  const naboNamn = kommunar.find((k) => k.kommune_code === nabo)?.kommune_name;
  const omraade = naboNamn ? `${naboNamn} og nabokommunane` : fylkeNamn || "landet";

  useEffect(() => {
    const valt = params.get("liste");
    if (valt) document.getElementById(`liste-${valt}`)?.scrollIntoView({ behavior: "smooth" });
  }, [params, lister]);

  return (
    <div className="min-h-screen bg-[#FAFBFC]">
      <header className="bg-white border-b border-[#E6E9F0]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="topplister-tilbake">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-[#1F2A36] flex items-center gap-2">
              <Trophy className={`w-5 h-5 ${retning === "best" ? "text-[#1E7A4B]" : "text-[#E52437]"}`} />{" "}
              {naboNamn
                ? `${naboNamn} og nabokommunane`
                : `${retning === "best" ? "Best" : "Verst"} i ${fylkeNamn || "landet"}`}
            </h1>
            <p className="text-sm text-[#8AA4BF]">
              {naboNamn
                ? `${naboNamn} og nabokommunane rangerte på dei viktigaste tala${
                    retning === "best" ? ", best først" : ", dårlegast først"
                  }`
                : retning === "best"
                ? `Kommunane i ${omraade} som gjer det best på dei viktigaste tala – gode døme å vise fram`
                : `Kommunane i ${omraade} som kjem dårlegast ut på dei viktigaste tala`}
            </p>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6" data-testid="topplister-side">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-full border border-[#E6E9F0] bg-white p-0.5">
            <button
              onClick={() => bytt("retning", "")}
              className={`px-3 py-1 rounded-full text-xs transition-colors ${
                retning === "verst" ? "bg-[#E52437] text-white" : "text-[#4B647F] hover:bg-[#F1F4F8]"
              }`}
              data-testid="toppliste-retning-verst"
            >
              Verst
            </button>
            <button
              onClick={() => bytt("retning", "best")}
              className={`px-3 py-1 rounded-full text-xs transition-colors ${
                retning === "best" ? "bg-[#1E7A4B] text-white" : "text-[#4B647F] hover:bg-[#F1F4F8]"
              }`}
              data-testid="toppliste-retning-best"
            >
              Best
            </button>
          </div>
          {naboNamn && (
            <button
              onClick={() => bytt("nabo", "")}
              className="px-3 py-1.5 rounded-full text-xs bg-[#103766] text-white inline-flex items-center gap-1.5"
              data-testid="toppliste-nabo-aktiv"
            >
              {naboNamn} og nabokommunane
              <X className="w-3 h-3" />
            </button>
          )}
          <span className="w-full" />
          <button
            onClick={() => bytt("fylke", "")}
            className={`px-3 py-1.5 rounded-full text-xs transition-colors ${
              !fylke && !nabo ? "bg-[#103766] text-white" : "bg-white border border-[#E6E9F0] text-[#4B647F] hover:bg-[#F1F4F8]"
            }`}
            data-testid="toppliste-fylke-heile-landet"
          >
            Heile landet
          </button>
          {fylker.map((f) => (
            <button
              key={f.fylke_code}
              onClick={() => bytt("fylke", f.fylke_code)}
              className={`px-3 py-1.5 rounded-full text-xs transition-colors ${
                fylke === f.fylke_code
                  ? "bg-[#103766] text-white"
                  : "bg-white border border-[#E6E9F0] text-[#4B647F] hover:bg-[#F1F4F8]"
              }`}
              data-testid={`toppliste-fylke-${f.fylke_code}`}
            >
              {f.fylke_name}
            </button>
          ))}
        </div>
        {lastar || lister.length === 0 ? (
          <p className="text-sm text-[#8AA4BF]">Hentar tal …</p>
        ) : (
          TEMA.map(({ id, label, icon: Ikon }) => {
            const temaLister = lister.filter((l) => (l.tema || "okonomi") === id);
            if (!temaLister.length) return null;
            return (
              <section key={id} id={`tema-${id}`} className="space-y-6" data-testid={`tema-${id}`}>
                <h2 className="flex items-center gap-2 text-base font-medium text-[#1F2A36] pt-2">
                  <Ikon className="w-4 h-4 text-[#103766]" /> {label}
                </h2>
                {temaLister.map((liste) => (
                  <div key={liste.id} id={`liste-${liste.id}`}>
                    <Liste
                      liste={liste}
                      spor={spor}
                      open={!!opne[liste.id]}
                      onToggle={() => setOpne((o) => ({ ...o, [liste.id]: !o[liste.id] }))}
                    />
                  </div>
                ))}
              </section>
            );
          })
        )}
      </main>
    </div>
  );
};

export default TopplisterPage;
